import supabase from "@/lib/supabase";
import { AnswerRecord, PracticeQuestion, Question } from "@/constant/schema";
import { computeNextReviewAt } from "@/constant/mistake";

interface AnswerItem {
  id: string;
  question_id: string;
  correct: boolean;
  answer: string;
  created_at: string;
}

interface PracticeItem {
  id: string;
  subject: string;
  questions: PracticeQuestion[];
  answers: AnswerItem[];
  progress: number;
  mode_type: string;
  place_id: string;
}

async function insertAnswer(question_id: string, user_id: string | null, correct: boolean, answer: string, mode_type: string, place_id: string, subject: string) {
  if (!user_id || !question_id || question_id === '') {
    return;
  }
  // 先找到该用户同一个question_id的回答历史纪录
  const { data: history, error: historyError } = await supabase
    .from('user_answers')
    .select('id, created_at, correct')
    .eq('user_id', user_id)
    .eq('question_id', question_id)
    .order('created_at', { ascending: true });

  if (historyError && historyError.code !== 'PGRST116') {
    console.error('Error getting user answer history:', historyError);
    throw historyError;
  }

  // 将当前回答和历史回答放在一起计算
  const allAnswers = [
    ...history || [],
    {
      id: 'current',
      created_at: new Date().toISOString(),
      correct: correct
    }
  ];
  
  const nextReviewAt = computeNextReviewAt(allAnswers);

  const { data, error } = await supabase
    .from('user_answers')
    .insert({
      question_id,
      user_id,
      correct,
      answer,
      mode_type,
      place_id,
      subject,
      next_review_at: nextReviewAt?.toISOString(),
    })
    .select('id')
    .single();
  if (error) {
    console.error('Error inserting answer:', error);
  }
  return data?.id;
}

function transferAnswersToAnswerRecords(answers: any[]): AnswerRecord[] {
  const groupedAnswers = answers.reduce((acc, answer) => {
    const questionId = answer.question_id;
    if (!acc[questionId]) {
      acc[questionId] = [];
    }
    acc[questionId].push(answer);
    return acc;
  }, {} as Record<string, any[]>);

  return Object.entries(groupedAnswers).map(([questionId, questionAnswers]) => {
    // 按 created_at 排序，最新的在前
    const sortedAnswers = (questionAnswers as any[]).sort((a: any, b: any) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const latestAnswer = sortedAnswers[0];
    const firstAnswer = sortedAnswers[sortedAnswers.length - 1];

    return {
      questionId: questionId,
      selectedOption: latestAnswer.answer,
      isCorrect: latestAnswer.correct,
      timestamp: latestAnswer.created_at,
      firstSelectedOption: firstAnswer.answer,
      firstIsCorrect: firstAnswer.correct
    };
  });
}

/**
 * 从 user_answers 表查询指定练习的答案记录
 * @param user_id 用户ID
 * @param question_ids 题目ID列表
 * @param mode_type 练习模式类型
 * @param place_id lesson_id 或 chapter_id
 * @returns AnswerRecord[] 格式的答案记录数组
 */
async function fetchAnswersFromUserAnswers(
  user_id: string,
  question_ids: string[],
  mode_type: string,
  place_id: string
): Promise<AnswerRecord[]> {
  if (!user_id || !question_ids || question_ids.length === 0) {
    return [];
  }

  try {
    // 查询 user_answers 表
    const { data: answers, error } = await supabase
      .from('user_answers')
      .select('*')
      .eq('user_id', user_id)
      .in('question_id', question_ids)
      .eq('mode_type', mode_type)
      .eq('place_id', place_id)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching answers from user_answers:', error);
      return [];
    }

    if (!answers || answers.length === 0) {
      return [];
    }

    // 使用现有的 transferAnswersToAnswerRecords 函数转换格式
    return transferAnswersToAnswerRecords(answers);
  } catch (error) {
    console.error('Exception in fetchAnswersFromUserAnswers:', error);
    return [];
  }
}

async function generateMistakePractice(user_id: string, subject: string, question_ids: string[]) {
  const { data, error } = await supabase
    .from('user_practice')
    .insert({
      user_id: user_id,
      subject: subject,
      questions: question_ids
    })
    .select()
    .single();
  if (error) {
    console.error('Error generating practice:', error);
    throw error;
  }
  return data.id;
}

async function generateSkillLessonPractice(user_id: string, lesson_id: string, difficulty: string) {
  const { data: skills, error: skillsError } = await supabase
    .from('syllabus_skill')
    .select('*')
    .eq('lesson_id', lesson_id);
  if (skillsError) {
    console.error('Error getting skill:', skillsError);
    throw skillsError;
  }
  // 根据要求从skills中抽取8道题，尽量每个skill都抽一道，优先pass，排除reject
  // skills: syllabus_skill[]
  // skill.question_ids 里面存储每个skill下的题目id数组
  // questions表有review_status属性

  // 首先获取每个skill下的有效题目（不为reject，优先pass）
  // questions 查询结果在 questions 变量，见本函数后续代码
  // questions 为题目的列表 [{ id, ..., review_status }, ...]，id与skill.question_ids关联

  const skillQuestionIds = skills.map((skill) => skill.question_ids).flat();
  const { data: questions, error: questionsError } = await supabase
    .from('questions')
    .select('*')
    .in('id', skillQuestionIds);
  if (questionsError) {
    console.error('Error getting questions:', questionsError);
  }

  // 1. 构建一个 questionsById 便于查询
  const questionsById: Record<string, any> = {};
  for (const q of questions || []) {
    questionsById[q.id] = q;
  }

  // 2. 为每个skill划分题目
  const skillToPassQuestions: Record<string, string[]> = {};
  const skillToOtherQuestions: Record<string, string[]> = {};
  for (const skill of skills) {
    const qids = Array.isArray(skill.question_ids) ? skill.question_ids : [];
    const pass: string[] = [];
    const other: string[] = [];
    for (const qid of qids) {
      const q = questionsById[qid];
      if (!q || q.review_status === 'reject') continue;
      if (q.review_status === 'pass') {
        pass.push(qid);
      } else {
        other.push(qid);
      }
    }
    skillToPassQuestions[skill.id] = pass;
    skillToOtherQuestions[skill.id] = other;
  }

  // 3. 遍历skills，优先每个skill抽一个pass题
  let chosen: Set<string> = new Set();
  let selectedQids: string[] = [];
  for (const skill of skills) {
    const passList = skillToPassQuestions[skill.id];
    if (passList && passList.length > 0) {
      const pick = passList.find(qid => !chosen.has(qid));
      if (pick) {
        chosen.add(pick);
        selectedQids.push(pick);
        if (selectedQids.length >= 8) break;
      }
    }
  }
  // 4. 如不足8题，继续每个skill抽一个非reject（非pass）的题
  if (selectedQids.length < 8) {
    for (const skill of skills) {
      const otherList = skillToOtherQuestions[skill.id];
      if (otherList && otherList.length > 0) {
        const pick = otherList.find(qid => !chosen.has(qid));
        if (pick) {
          chosen.add(pick);
          selectedQids.push(pick);
          if (selectedQids.length >= 8) break;
        }
      }
    }
  }
  // 5. 兜底如果还不足8题，从所有剩下的题中按优先级填满
  if (selectedQids.length < 8) {
    // 所有还没选过、非reject、优先pass然后other
    const allPass = (questions || []).filter(q => q.review_status === 'pass' && !chosen.has(q.id));
    for (const q of allPass) {
      selectedQids.push(q.id);
      chosen.add(q.id);
      if (selectedQids.length >= 8) break;
    }
    if (selectedQids.length < 8) {
      const allOther = (questions || []).filter(q => q.review_status !== 'reject' && q.review_status !== 'pass' && !chosen.has(q.id));
      for (const q of allOther) {
        selectedQids.push(q.id);
        chosen.add(q.id);
        if (selectedQids.length >= 8) break;
      }
    }
  }
  // 最终只保留8个
  selectedQids = selectedQids.slice(0, 8);


}

async function getPracticeById(practice_id: string): Promise<PracticeItem | null> {
  const { data, error } = await supabase
    .from('user_practice')
    .select('*')
    .eq('id', practice_id)
    .single();
  if (error) {
    console.error('Error getting practice:', error);
    throw error;
  }
  const { data: questionsData, error: questionsError } = await supabase
    .from('questions')
    .select('*')
    .in('id', data.questions);
  if (questionsError) {
    console.error('Error getting questions:', questionsError);
  }
  const { data: answersData, error: answersError } = await supabase
    .from('user_answers')
    .select('*')
    .in('id', data.answers);
  if (answersError) {
    console.error('Error getting answers:', answersError);
  }
  const questions = questionsData ? questionsData.map((question) => ({
    id: question.id,
    type: question.type,
    question: question.question,
    questions: question.questions,
    answer: question.answer,
    explanation: question.explanation,
    options: question.options,
    question_type: question.type,
    review_status: question.review_status,
    reject_reason: question.reject_reason,
    wasWrong: false
  })) : [];

  const answers = answersData ? answersData.map((answer) => ({
    id: answer.id,
    question_id: answer.question_id,
    correct: answer.correct,
    answer: answer.answer,
    created_at: answer.created_at
  })) : [];
  return {
    id: data.id,
    subject: data.subject,
    questions: questions,
    answers: answers,
    progress: data.progress,
    mode_type: data.mode_type,
    place_id: data.place_id
  };
}

async function updatePracticeProgress(practice_id: string, progress: number, appendAnswerId: string) {
  if (!practice_id) {
    return;
  }
  const { data: answersData, error: answersError } = await supabase
    .from('user_practice')
    .select('answers')
    .eq('id', practice_id)
    .single();

  if (answersError) {
    console.error('Error getting answers:', answersError);
    throw answersError;
  }

  let answers;

  if (answersData.answers) {
    answers = answersData.answers;
    if (answers.includes(appendAnswerId)) {
      return;
    } else {
      answers.push(appendAnswerId);
    }
  } else {
    answers = [appendAnswerId];
  }

  const { data, error } = await supabase
    .from('user_practice')
    .update({
      progress: progress,
      answers: answers
    })
    .eq('id', practice_id);
  if (error) {
    console.error('Error updating practice progress:', error);
    throw error;
  }
  return data;
}

// Module-level cache populated by fetchElementsFromSkills to eliminate N+1 queries
const questionCache = new Map<string, Question>();

export function preloadQuestionsToCache(questions: Question[]): void {
  for (const question of questions) {
    if (question?.id) {
      questionCache.set(question.id, question);
    }
  }
}

async function getQuestionById(question_id: string): Promise<Question | null> {
  if (questionCache.has(question_id)) {
    return questionCache.get(question_id) || null;
  }
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('id', question_id)
    .single();
  if (error) {
    console.error('Error getting question:', error);
  }
  if (data) {
    questionCache.set(question_id, data);
  }
  return data;
}

async function getQuestionsByIds(question_ids: string[]): Promise<Question[]> {
  if (!question_ids || question_ids.length === 0) {
    return [];
  }
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .in('id', question_ids);
  if (error) {
    console.error('Error getting questions:', error);
    throw error;
  }
  return data || [];
}

export { insertAnswer, generateMistakePractice as generatePractice, getPracticeById, updatePracticeProgress, transferAnswersToAnswerRecords, fetchAnswersFromUserAnswers, getQuestionById, getQuestionsByIds };